export type ReinforcedConcreteValues = Record<string, string>;

export type DerivedQuantity = {
  key: string;
  label: string;
  unit: string;
  resourceCode: string | null;
  quantity: number | null;
  basis: "parameter" | "derived" | "pending";
  note: string;
};

function numberValue(values: ReinforcedConcreteValues, key: string) {
  const raw = values[key];
  if (!raw) return null;
  const value = Number(raw.replace(",", "."));
  return Number.isFinite(value) ? value : null;
}

function positive(value: number | null) {
  return value !== null && value > 0 ? value : null;
}

function pctFactor(value: number | null) {
  return 1 + Math.max(0, value ?? 0) / 100;
}

function derivedFormworkRatio(
  workUnitCode: string,
  values: ReinforcedConcreteValues,
): { value: number | null; note: string } {
  const manual = positive(numberValue(values, "formwork_ratio_m2_m3"));
  if (manual !== null) {
    return { value: manual, note: "Valor confirmado manualmente." };
  }

  if (values.formwork_system === "none") {
    return { value: 0, note: "Sem cofragem, conforme parâmetro selecionado." };
  }

  if (workUnitCode === "BA.VERT.PILAR") {
    const a = positive(numberValue(values, "section_a"));
    const b = positive(numberValue(values, "section_b"));
    if (a && b) {
      return {
        value: (2 * (a + b)) / (a * b),
        note: "Derivado geometricamente para quatro faces do pilar.",
      };
    }
  }

  if (workUnitCode === "BA.VERT.MURO") {
    const thickness = positive(numberValue(values, "thickness"));
    const faces = values.formwork_faces === "one" ? 1 : values.formwork_faces === "two" ? 2 : null;
    if (thickness && faces) {
      return {
        value: faces / thickness,
        note: "Derivado da espessura e do número de faces cofradas.",
      };
    }
  }

  if (workUnitCode === "BA.HOR.LAJE.MACICA") {
    const thickness = positive(numberValue(values, "thickness"));
    if (thickness) {
      return {
        value: 1 / thickness,
        note: "Derivado para cofragem do intradorso; bordos não incluídos.",
      };
    }
  }

  if (workUnitCode === "BA.FND.SAPATA.ISOLADA") {
    const length = positive(numberValue(values, "length"));
    const width = positive(numberValue(values, "width"));
    if (length && width) {
      return {
        value: (2 * (length + width)) / (length * width),
        note: "Derivado para faces laterais; confirmar se a escavação dispensa cofragem.",
      };
    }
  }

  return {
    value: null,
    note: "Necessita confirmação manual para esta geometria.",
  };
}

export function deriveReinforcedConcreteQuantities(
  workUnitCode: string,
  values: ReinforcedConcreteValues,
): DerivedQuantity[] {
  const concreteExcess = numberValue(values, "concrete_excess_pct");
  const steelWaste = numberValue(values, "steel_waste_pct");
  const reinforcement = positive(numberValue(values, "reinforcement_kg_m3"));
  const formwork = derivedFormworkRatio(workUnitCode, values);

  const concreteResourceCode = values.concrete_strength
    ? "MAT.BET." + values.concrete_strength.replace("/", "_")
    : null;

  const steelResourceCode =
    values.steel_grade && values.steel_grade !== "other"
      ? "MAT.ACO." + values.steel_grade
      : null;

  const formworkResourceCode =
    values.formwork_system === "recoverable_metal"
      ? "AUX.COFR.REC_METAL"
      : values.formwork_system === "recoverable_wood"
        ? "AUX.COFR.REC_MADEIRA"
        : values.formwork_system === "lost"
          ? "AUX.COFR.PERDIDA"
          : null;

  const equipmentResourceCode =
    values.placement_method === "pump"
      ? "EQ.BET.BOMBA"
      : values.placement_method === "crane"
        ? "EQ.BET.GRUA"
        : null;

  return [
    {
      key: "concrete",
      label: values.concrete_strength
        ? "Betão " + values.concrete_strength
        : "Betão",
      unit: "m³",
      resourceCode: concreteResourceCode,
      quantity: pctFactor(concreteExcess),
      basis: "derived",
      note:
        "Base de 1,000 m³ por m³ de unidade de obra, acrescida do excesso/desperdício indicado.",
    },
    {
      key: "reinforcement",
      label: values.steel_grade
        ? "Aço " + values.steel_grade
        : "Aço para armaduras",
      unit: "kg",
      resourceCode: steelResourceCode,
      quantity: reinforcement === null ? null : reinforcement * pctFactor(steelWaste),
      basis: reinforcement === null ? "pending" : "parameter",
      note:
        reinforcement === null
          ? "Falta indicar kg de aço por m³."
          : "Quantidade indicada pelo orçamentista, acrescida do desperdício definido.",
    },
    {
      key: "formwork",
      label: "Cofragem",
      unit: "m²",
      resourceCode: formworkResourceCode,
      quantity: formwork.value,
      basis:
        positive(numberValue(values, "formwork_ratio_m2_m3")) !== null
          ? "parameter"
          : formwork.value === null
            ? "pending"
            : "derived",
      note: formwork.note,
    },
    {
      key: "labor",
      label: "Mão de obra",
      unit: "h",
      resourceCode: null,
      quantity: null,
      basis: "pending",
      note:
        "Rendimentos de armador, cofrador, ajudantes e betonagem serão definidos pela composição L37.",
    },
    {
      key: "equipment",
      label:
        values.placement_method === "pump"
          ? "Equipamentos — bomba"
          : values.placement_method === "crane"
            ? "Equipamentos — grua"
            : "Equipamentos de betonagem",
      unit: "h",
      resourceCode: equipmentResourceCode,
      quantity: null,
      basis: "pending",
      note:
        "O rendimento do equipamento será definido pela composição L37 e pelas condições da obra.",
    },
  ];
}
